import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enUS } from 'date-fns/locale/en-US';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { useHseWeeklyStore, type HseNotice } from '../../stores/hse-weekly.store';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import HseWeekStatusBar from '../../components/hse/HseWeekStatusBar';
import HseEvidencePanel from '../../components/hse/HseEvidencePanel';
import HseHistoryDrawer, { type HseDrawerTab } from '../../components/hse/HseHistoryDrawer';
import { HseReopenDialog, HseSubmitDialog } from '../../components/hse/HseWeekActionDialogs';
import { formatWeekRange } from '../../utils/hseFormat';
import { BranchSiteSelect } from '../../components/data-entry/BranchMonthToolbar';
import HseWeeklyReportTable from '../../components/hse/HseWeeklyReportTable';
import ReportingWeekPicker from '../../components/hse/ReportingWeekPicker';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { HELP_GUIDE_HSE_TERMS, HELP_GUIDE_HSE_WEEKLY } from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../../utils/helpGuideSession';

type HseTourPhase = 'branch' | 'table' | 'save';

/** Legacy-role fallback for reopening a submitted week: organization admins only,
 * mirroring the backend's `recyclee.{org}.*` requirement. */
const HSE_REOPEN_LEGACY_ROLES = ['admin', 'waste_outlet_admin', 'recycler_admin', 'truck_co_admin', 'charity_admin'] as const;

const HseWeeklyReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizationStore();
  const {
    sites,
    selectedSiteId,
    selectedWeekStart,
    draft,
    exposure,
    report,
    evidence,
    history,
    weeks,
    isLoadingSites,
    isLoadingReport,
    isSaving,
    isSubmitting,
    isUploadingEvidence,
    isLoadingHistory,
    isLoadingWeeks,
    notice,
    clearNotice,
    loadPreferences,
    resetForOrganizationChange,
    fetchSites,
    setSelectedSiteId,
    setSelectedWeekStart,
    updateDraftRow,
    updateExposure,
    saveReport,
    submitWeek,
    reopenWeek,
    uploadEvidence,
    removeEvidence,
    loadHistory,
    loadWeeks,
  } = useHseWeeklyStore();
  const { canMutate } = useAuthPermissions();

  const [drawerTab, setDrawerTab] = useState<HseDrawerTab | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [helpGuide, setHelpGuide] = useState<string | null>(null);
  const [hseTourPhase, setHseTourPhase] = useState<HseTourPhase>('branch');

  const showBranchTip = helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'branch';
  const showTableTip =
    (helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'table') ||
    helpGuide === HELP_GUIDE_HSE_TERMS;
  const showSaveTip = helpGuide === HELP_GUIDE_HSE_WEEKLY && hseTourPhase === 'save';
  const showHseTermsTip = helpGuide === HELP_GUIDE_HSE_TERMS;

  const finishHelpGuide = () => {
    setHelpGuide(null);
    setHseTourPhase('branch');
    clearHelpGuideActive();
  };

  const advanceHelpGuide = () => {
    if (helpGuide === HELP_GUIDE_HSE_TERMS) {
      finishHelpGuide();
      return;
    }
    if (hseTourPhase === 'branch') {
      setHseTourPhase('table');
      return;
    }
    if (hseTourPhase === 'table') {
      setHseTourPhase('save');
      return;
    }
    finishHelpGuide();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    // Accept legacy `hse-fill` as the weekly guide.
    if (
      guide !== HELP_GUIDE_HSE_WEEKLY &&
      guide !== 'hse-fill' &&
      guide !== HELP_GUIDE_HSE_TERMS
    ) {
      return;
    }
    if (guide === HELP_GUIDE_HSE_TERMS) {
      markHelpGuideActive(HELP_GUIDE_HSE_TERMS);
      setHelpGuide(HELP_GUIDE_HSE_TERMS);
      setHseTourPhase('table');
    } else {
      markHelpGuideActive(HELP_GUIDE_HSE_WEEKLY);
      setHelpGuide(HELP_GUIDE_HSE_WEEKLY);
      setHseTourPhase(guide === 'hse-fill' ? 'table' : 'branch');
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!helpGuide) return;
    const id =
      hseTourPhase === 'branch'
        ? 'help-hse-branch'
        : hseTourPhase === 'table'
          ? 'help-hse-table'
          : 'help-hse-save';
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [helpGuide, hseTourPhase]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  useEffect(() => {
    if (!selectedOrganization?.id) return;
    resetForOrganizationChange();
    void fetchSites(selectedOrganization.id);
  }, [selectedOrganization?.id, resetForOrganizationChange, fetchSites]);

  const showBlockingLoader = (isLoadingSites && sites.length === 0) || isLoadingReport;
  const submitted = report?.status === 'submitted';
  const busy = isSaving || isSubmitting || isLoadingReport;
  const editingDisabled = !selectedSiteId || busy || submitted;
  const canReopen = Boolean(selectedOrganization?.id) && canMutate(`recyclee.${selectedOrganization?.id}.*`, HSE_REOPEN_LEGACY_ROLES);
  const weekLabel = useMemo(() => formatWeekRange(selectedWeekStart, i18n.language), [selectedWeekStart, i18n.language]);

  const handleSave = useCallback(async () => {
    await saveReport();
  }, [saveReport]);

  const openDrawer = (tab: HseDrawerTab) => {
    setDrawerTab(tab);
    if (tab === 'history') void loadHistory();
    else void loadWeeks();
  };

  const noticeText = (n: HseNotice | null): string => {
    if (!n) return '';
    if (n.text) return n.text;
    const params = { ...(n.params ?? {}) };
    if (typeof params.term === 'string') params.term = t(`hse.terms.${params.term}.label`);
    return t(n.key ?? '', params);
  };

  if (!selectedOrganization) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {t('hse.title')}
        </Typography>
        <Alert severity="info">{t('hse.selectOrganization')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {(isLoadingSites || isLoadingReport) && <LinearProgress sx={{ mb: 2 }} />}

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <PageHeader
          icon={<HealthAndSafetyOutlinedIcon />}
          title={t('hse.title')}
          actions={
            <SetupCoachmark
              open={showBranchTip}
              title={tHelp('knowledgeBase.hseBranchGuideTitle')}
              subtitle={tHelp('knowledgeBase.hseBranchGuideSubtitle')}
              onDismiss={finishHelpGuide}
              onNext={advanceHelpGuide}
              nextLabel={t('selfSetup.tourNext')}
              placement="left"
            >
              <Box
                id="help-hse-branch"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  gap: 2,
                  rowGap: 1.5,
                  maxWidth: '100%',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 220px',
                    minWidth: 180,
                    maxWidth: 280,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <BranchSiteSelect
                    sites={sites}
                    selectedSiteId={selectedSiteId}
                    onSiteChange={setSelectedSiteId}
                    disabled={showBlockingLoader || isSaving}
                  />
                </Box>
                <Box
                  sx={{
                    flex: '1 1 220px',
                    minWidth: 180,
                    maxWidth: 280,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <ReportingWeekPicker
                    selectedWeekStart={selectedWeekStart}
                    onWeekChange={setSelectedWeekStart}
                    disabled={showBlockingLoader || isSaving}
                  />
                </Box>
              </Box>
            </SetupCoachmark>
          }
        />
      </LocalizationProvider>

      <HseWeekStatusBar
        report={report}
        exposure={exposure}
        onExposureChange={updateExposure}
        readOnly={submitted}
        disabled={!selectedSiteId || busy}
        onOpenHistory={() => openDrawer('history')}
        onOpenWeeks={() => openDrawer('weeks')}
      />

      {submitted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {canReopen ? t('hse.status.submittedReadOnlyCanReopen') : t('hse.status.submittedReadOnly')}
        </Alert>
      )}

      {sites.length === 0 && !isLoadingSites && (
        <Alert severity="warning" icon={<StorefrontOutlinedIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {t('common.noBranchesForOrg')}
        </Alert>
      )}

      <Box id="help-hse-table">
        <SetupCoachmark
          open={showTableTip}
          title={
            showHseTermsTip
              ? tHelp('knowledgeBase.hseTermsGuideTitle')
              : tHelp('knowledgeBase.hseFillGuideTitle')
          }
          subtitle={
            showHseTermsTip
              ? tHelp('knowledgeBase.hseTermsGuideSubtitle')
              : tHelp('knowledgeBase.hseFillGuideSubtitle')
          }
          onDismiss={finishHelpGuide}
          onNext={advanceHelpGuide}
          nextLabel={
            showHseTermsTip ? t('selfSetup.tourDone') : t('selfSetup.tourNext')
          }
          fullWidth
        >
          <HseWeeklyReportTable
            draft={draft}
            onRowChange={updateDraftRow}
            disabled={editingDisabled}
          />
        </SetupCoachmark>
      </Box>

      <HseEvidencePanel
        files={evidence}
        readOnly={submitted}
        disabled={!selectedSiteId || busy}
        uploading={isUploadingEvidence}
        onUpload={(files, termKey) => void uploadEvidence(files, termKey)}
        onRemove={(id) => void removeEvidence(id)}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
        {submitted ? (
          canReopen && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<LockOpenOutlinedIcon />}
              onClick={() => setReopenOpen(true)}
              disabled={busy}
            >
              {t('hse.reopen.button')}
            </Button>
          )
        ) : (
          <Button
            variant="outlined"
            size="large"
            startIcon={<SendOutlinedIcon />}
            onClick={() => setSubmitOpen(true)}
            disabled={editingDisabled}
          >
            {t('hse.submit.button')}
          </Button>
        )}
        <SetupCoachmark
          open={showSaveTip}
          title={tHelp('knowledgeBase.hseSaveGuideTitle')}
          subtitle={tHelp('knowledgeBase.hseSaveGuideSubtitle')}
          onDismiss={finishHelpGuide}
          onNext={finishHelpGuide}
          nextLabel={t('selfSetup.tourDone')}
          placement="left"
        >
          <Button
            id="help-hse-save"
            variant="contained"
            size="large"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => void handleSave()}
            disabled={editingDisabled}
          >
            {t('hse.saveButton')}
          </Button>
        </SetupCoachmark>
      </Box>

      <HseHistoryDrawer
        open={drawerTab !== null}
        tab={drawerTab ?? 'history'}
        onTabChange={openDrawer}
        onClose={() => setDrawerTab(null)}
        weekLabel={weekLabel}
        history={history}
        weeks={weeks}
        loadingHistory={isLoadingHistory}
        loadingWeeks={isLoadingWeeks}
        selectedWeekStart={selectedWeekStart}
        onOpenWeek={(week) => {
          setDrawerTab(null);
          setSelectedWeekStart(week);
        }}
      />

      <HseSubmitDialog
        open={submitOpen}
        weekLabel={weekLabel}
        busy={isSubmitting || isSaving}
        onCancel={() => setSubmitOpen(false)}
        onConfirm={async () => {
          if (await submitWeek()) setSubmitOpen(false);
        }}
      />
      <HseReopenDialog
        open={reopenOpen}
        weekLabel={weekLabel}
        busy={isSubmitting}
        onCancel={() => setReopenOpen(false)}
        onConfirm={async (reason) => {
          if (await reopenWeek(reason)) setReopenOpen(false);
        }}
      />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={6000}
        onClose={clearNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clearNotice} severity={notice?.tone ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {noticeText(notice)}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HseWeeklyReport;
